import { useQuery } from "@tanstack/react-query";
import React from "react";
import useApi from "../../../../shared/hooks/useApi";
import {
  contributeAiCapabilityParamOption,
  declareAiCapability,
  useProvideAiCapability,
} from "../../../ai-omnibar/ai-capability-registry";

/**
 * What the administrative payment invoicing page can do for the AI omnibar.
 *
 * The patient picker takes ids from a typeahead rather than a list, so it is left out: turning a
 * name into one of those ids would mean guessing which patient was meant.
 */
declareAiCapability({
  id: "administrativePayments.filter",
  title: "Filter the administrative payments list",
  effect: "prefill",
  description:
    "Sets the filters on Billing > Administrative Payments: the date range, contract, office and whether the invoice is new or an adjustment. The page opens on the current month. Only works while that page is open.",
  location: "the filter bar of the Billing > Administrative Payments page",
  scope: "page",
  permissionKey: "view_billing_section",
  params: [
    {
      name: "from",
      type: "date",
      required: false,
      description: "First day to include, as yyyy-MM-dd.",
    },
    {
      name: "to",
      type: "date",
      required: false,
      description: "Last day to include, as yyyy-MM-dd.",
    },
    {
      name: "contract",
      type: "enum",
      required: false,
      multiple: true,
      description: "Limit to these contracts.",
    },
    {
      name: "office",
      type: "enum",
      required: false,
      multiple: true,
      description: "Limit to these offices.",
    },
    {
      name: "invoiceType",
      type: "enum",
      required: false,
      multiple: true,
      description: "Whether the invoice is a new one or an adjustment.",
      options: [
        { value: "NEW", label: "New" },
        { value: "ADJUSTMENT", label: "Adjustment" },
      ],
    },
  ],
  examples: [
    "administrative payments this month",
    "adjustment invoices for the france contract",
    "administrative payments for the europe office",
  ],
});

function useContributedOptions(
  paramName: string,
  rows: readonly { id: string | number; name: string }[] | undefined
): void {
  const key = (rows ?? []).map((row) => `${row.id}:${row.name}`).join("|");

  React.useEffect(() => {
    const releases = (rows ?? []).map((row) =>
      contributeAiCapabilityParamOption("administrativePayments.filter", paramName, {
        value: String(row.id),
        label: row.name,
      })
    );

    return () => releases.forEach((release) => release());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramName, key]);
}

export function useAdministrativePaymentAiCapabilities(params: {
  /** The page's own filter setter, typed loosely: this hook only sets keys it declares. */
  setFilter: (key: string, value: unknown) => void;
}): void {
  const { setFilter } = params;
  const { queries } = useApi();

  const setFilterRef = React.useRef(setFilter);
  setFilterRef.current = setFilter;

  const offices = useQuery(queries.office.list());
  const contracts = useQuery(queries.contract.list());

  useContributedOptions("office", offices.data?.offices);
  useContributedOptions("contract", contracts.data?.contractTypes);

  // Not available until the lists have arrived: an empty option list turns a real filter into a
  // free-text search, so "europe" would fall through instead of setting the office filter.
  useProvideAiCapability(
    "administrativePayments.filter",
    (args, context) => {
      const applied: string[] = [];
      const asList = (value: unknown) => (Array.isArray(value) ? value : [value]);
      const label = (name: string) => context.labels[name] ?? String(args[name]);

      if (args.from !== undefined) {
        setFilterRef.current("from", String(args.from));
      }

      if (args.to !== undefined) {
        setFilterRef.current("to", String(args.to));
      }

      if (args.from !== undefined || args.to !== undefined) {
        applied.push([args.from, args.to].filter(Boolean).join(" to "));
      }

      for (const [arg, key] of [
        ["contract", "contractTypeIds"],
        ["office", "officeIds"],
      ] as const) {
        if (args[arg] === undefined) {
          continue;
        }

        setFilterRef.current(
          key,
          asList(args[arg]).map((one) => Number(one))
        );
        applied.push(label(arg));
      }

      if (args.invoiceType !== undefined) {
        const types = asList(args.invoiceType).map((one) => {
          const type = String(one).toUpperCase();

          if (type !== "NEW" && type !== "ADJUSTMENT") {
            throw new Error(
              `An administrative payment invoice is new or an adjustment, not ${type}.`
            );
          }

          return type;
        });

        setFilterRef.current("invoiceTypes", types);
        applied.push(types.map((type) => type.toLowerCase()).join(" and "));
      }

      return applied.length === 0
        ? "Re-applied the administrative payment filters"
        : `Showing administrative payments: ${applied.join(", ")}`;
    },
    { enabled: offices.isSuccess && contracts.isSuccess }
  );
}
